import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Moon, Sun, X } from "lucide-react";
import Logo from "./Logo";
import { scrollToId } from "./Reveal";

const LINKS = [
  { id: "services", label: "Services", testid: "navbar-link-services" },
  { id: "gallery", label: "Work", testid: "navbar-link-gallery" },
  { id: "manifesto", label: "Process", testid: "navbar-link-manifesto" },
  { id: "careers", label: "Careers", testid: "navbar-link-careers" },
  { id: "contact", label: "Contact", testid: "navbar-link-contact" },
];

export default function Navbar({ theme, toggleTheme }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-md transition-colors duration-300 bg-background/80 ${
          scrolled ? "border-border" : "border-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-8">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Strovyn home">
            <Logo />
          </button>

          <nav className="hidden items-center gap-8 md:flex">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={l.testid}
                onClick={() => go(l.id)}
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-accent"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              data-testid="theme-toggle-button"
              onClick={toggleTheme}
              aria-label="Toggle dark mode"
              className="flex h-9 w-9 items-center justify-center border border-border text-foreground transition-colors hover:border-accent hover:text-accent"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button
              data-testid="navbar-quote-button"
              onClick={() => go("contact")}
              className="hidden bg-foreground px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-background transition-colors hover:bg-accent hover:text-white sm:block"
            >
              Get a Quote
            </button>
            <button
              className="flex h-9 w-9 items-center justify-center border border-border md:hidden"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-button"
            >
              <Menu className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[100] flex flex-col bg-accent text-white"
            data-testid="mobile-menu-overlay"
          >
            <div className="flex h-16 items-center justify-between px-4 sm:px-8">
              <span className="font-serif text-2xl uppercase tracking-tight">
                STROVYN.
              </span>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                className="flex h-10 w-10 items-center justify-center"
              >
                <X className="h-7 w-7" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-3 px-6">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ x: -48, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => go(l.id)}
                  data-testid={`mobile-${l.testid}`}
                  className="text-left font-serif text-6xl uppercase leading-none tracking-tight transition-transform hover:translate-x-2"
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <p className="px-6 pb-8 text-center font-mono text-[10px] uppercase tracking-[0.35em] text-white/60">
              A new standard in innovation
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
